import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Card,
  CardContent,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import dayjs from "dayjs";
import moment from "moment";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import ResmedSignIn from "src/containers/SignInPage/ResmedSignIn";
import AuthService from "src/services/authService";
import UserService from "src/services/userService";
import { useQuery } from "src/services/utils";
import { injectAuthUser, updateAuthUser } from "src/store/actions/auth";

import LoadingButton from "@components/LoadingButton";
import StepTracker from "@components/StepTracker/StepTracker";

import ReferralWelcomeCard from "@containers/Cards/ReferralWelcomeCard";
import SleepCard from "@containers/Cards/SleepCard";
import CodeVerification, {
  CODE_VERIFICATION_FORM_ID,
} from "@containers/Forms/RegistrationForm/CodeVerification";
import EmailVerificationForm, {
  EMAIL_VERIF_FORM_ID,
} from "@containers/Forms/RegistrationForm/EmailVerificationForm";
import PatientDetailsForm, {
  PATIENT_DETAILS_FORM_ID,
} from "@containers/Forms/RegistrationForm/PatientDetailsForm";

import { getRecordId, handleDynamicFormSubmission } from "@utils/helpers";
import { LStorage } from "@utils/localStorage";

import NoAvailabilityPrompt from "./NoAvailabilityPrompt";

const STEPS = {
  register: 1,
  emailSignup: 2,
  verification: 3,
  demographic: 4,
  confirmation: 5,
};

const resmedLogoPath = "/assets/external-logos/resmed-logo-large.svg";
const totalSteps = Object.values(STEPS).length;
const GenericRegistration = ({ initialData }: { initialData: object }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();
  const partnerId = query.get("partnerId");
  const resmedRegistrationPath = location.pathname.includes("/resmed");

  const [step, setStep] = useState(() => STEPS.register);
  const [formData, setFormData] = useState<Record<string, any>>(
    () => initialData,
  );

  const [submitting, setSubmitting] = useState(false);
  const [confirmedAccount, setConfirmedAccount] = useState(false);
  const [showResmedModal, setShowResmedModal] = useState(false);

  const updateStep = (increment: boolean) => {
    if (step === totalSteps) return null;
    else if (increment) setStep(step + 1);
    else if (!confirmedAccount) setStep(step - 1);
  };

  const sendVerificationCode = (email: string) =>
    UserService.sendVerificationCode(email, "signUp", "email");

  const checkEmailAndSendCode = async (email: string) => {
    setSubmitting(true);

    try {
      const res = await UserService.checkPatientExistsByEmail(
        email.toLowerCase(),
      );
      if (res.data) {
        history.push(`/signInPage?email=${encodeURIComponent(email)}`);
      } else {
        await UserService.sendVerificationCode(email, "signUp", "email");
        setFormData((state) => ({ ...state, email }));
        updateStep(true);
        setSubmitting(false);
      }
    } catch (error) {
      setSubmitting(false);
      toast.error(
        "We're unable to send a verification code to this email at this time!",
      );
    }
  };

  const createUser = (data: any) => {
    setSubmitting(true);

    let refId = formData?.refId || partnerId;
    if (resmedRegistrationPath) refId = "resmed";

    const contactId = query.get("contactId") as string;

    const credentials = {
      password: data?.password,
      email: formData?.email,
    };

    const carrier = formData?.insuranceCarrier;
    const insuranceCards =
      carrier && carrier !== "Carrier Not Listed"
        ? [{ insuranceCarrier: carrier }]
        : [];

    const referralMetadata = LStorage.get("referralMetadata", undefined);
    UserService.signUp({
      ...credentials,
      insuranceCards,
      state: formData?.state || "",
      lastName: formData?.lastName || "",
      firstName: formData?.firstName || "",
      platform: "Web-Patient",
      partner: refId || "",
      metadata: referralMetadata,
      verificationCode: data.verificationCode,
      contacts: contactId ? [contactId] : [],
    })
      .then(async (res) => {
        const auth = await AuthService.login(credentials);
        const loginData = auth?.data;

        await AuthService.saveAuth(loginData);
        dispatch(injectAuthUser(loginData?.user));
        setConfirmedAccount(true);

        // Remove resmed metadata
        LStorage.remove("referralMetadata");

        await UserService.updateProfile(
          {
            chatbotProgress: {
              sheepa: 16,
              sheepaFinished: false,
              sheepaTime: moment().utc(),
            },
          },
          getRecordId(loginData?.user),
        )
          .then((res) => {
            AuthService.updateUserInfo(res.data);
            dispatch(updateAuthUser(res.data));
          })
          .catch(() => {});

        if (contactId)
          await UserService.setFavoriteContact(data?.user?.id, contactId);

        const updatedUser = {
          contacts: contactId ? [contactId] : [],
        };

        AuthService.updateUserInfo(updatedUser);
        dispatch(updateAuthUser(updatedUser));
        updateStep(true);
      })
      .catch((err) =>
        toast.error("We are unable to verify the code or create your account"),
      )
      .finally(() => setSubmitting(false));
  };

  const updatePatientProfile = async (values: any) => {
    const user = AuthService.getAuth()?.user;
    const patientId = getRecordId(user);

    // Check termsConsent; process consent if false
    let termsConsent = user?.termsConsent || false;
    if (!termsConsent) {
      termsConsent = await UserService.signConsentDocuments(patientId)
        .then((res) => res?.data === true)
        .catch(() => false);
    }

    await UserService.updateProfile(
      {
        ...values,
        termsConsent,
        timeZone: dayjs.tz.guess(),
        chatbotProgress: {
          sheepa: 16,
          sheepaFinished: false,
          sheepaTime: moment().utc(),
        },
      },
      patientId,
    )
      .then((res) => {
        AuthService.updateUserInfo(res.data);
        dispatch(updateAuthUser(res.data));
        updateStep(true);
      })
      .catch((error) => toast.error(error.message || "Unable to save details"));
  };

  return (
    <Box sx={{ padding: 1 }}>
      <Box paddingTop={0.5} paddingBottom={2.5}>
        <StepTracker
          totalSteps={totalSteps}
          currentStep={step}
          barHeight={6}
          gap={1}
        />
      </Box>

      <Grid container spacing={3}>
        {step >= 2 && step < 5 && (
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              {partnerId && <ReferralWelcomeCard partnerId={partnerId} />}
              <SleepCard />
            </Stack>
          </Grid>
        )}

        {step === 1 && (
          <Grid item xs={12} justifyItems="center">
            <NoAvailabilityPrompt onSubmit={() => updateStep(true)} />
          </Grid>
        )}

        {step === 2 && (
          <Grid item xs={12} md={6}>
            <Typography variant="body1">
              Please enter your email to register. We'll send you a verification
              code to get the process started.
            </Typography>
            <EmailVerificationForm
              data={{ email: formData.email }}
              onSubmit={(data) => checkEmailAndSendCode(data.email)}
            />

            {location.pathname.includes("/resmed") && (
              <>
                <Typography variant="h6" fontWeight={500} pt={2}>
                  Do you have a Resmed account?
                </Typography>
                <Typography variant="body1" pb={1}>
                  You can proceed by logging in with your Resmed account!
                </Typography>
                <Button
                  fullWidth
                  sx={{
                    padding: 1,
                    borderRadius: 1,
                    backgroundColor: "background.light",
                    fontSize: "1rem",
                  }}
                  onClick={() => setShowResmedModal(true)}
                >
                  <img
                    src={resmedLogoPath}
                    alt="Resmed Logo"
                    style={{ width: "90px", marginLeft: "10px" }}
                  />
                </Button>
                <Dialog
                  PaperProps={{
                    style: {
                      backgroundColor: "transparent",
                      boxShadow: "none",
                    },
                  }}
                  open={showResmedModal}
                  onClose={() => setShowResmedModal(false)}
                >
                  <IconButton
                    sx={{
                      position: "absolute",
                      left: "90%",
                      top: "6%",
                      zIndex: 100,
                      width: 28,
                      height: 28,
                    }}
                    onClick={() => setShowResmedModal(false)}
                  >
                    <CloseIcon fontSize="medium" />
                  </IconButton>

                  <ResmedSignIn
                    source="registration"
                    registrationFormData={formData}
                    handleRegistrationLogin={(data) => {
                      setFormData((state) => ({ ...state, ...data }));
                      setShowResmedModal(false);
                      setConfirmedAccount(true);
                      setStep(STEPS.demographic);
                    }}
                  />
                </Dialog>
              </>
            )}
          </Grid>
        )}

        {step === 3 && (
          <Grid item xs={12} md={6}>
            <CodeVerification
              resendCode={() => sendVerificationCode(formData.email)}
              onSubmit={(data) => {
                setFormData((state) => ({ ...state, ...data }));
                createUser({ ...data, email: formData.email });
              }}
            />
          </Grid>
        )}

        {step === 4 && (
          <Grid item xs={12} md={6}>
            <PatientDetailsForm
              onSubmit={(data) => {
                setFormData((state) => ({ ...state, ...data }));
                updatePatientProfile(data);
              }}
            />
          </Grid>
        )}

        {step === 5 && (
          <Grid item xs={12} justifyItems="center">
            <Card sx={{ maxWidth: 700, paddingY: 2, paddingX: 1 }}>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h5" pb={1} fontWeight={600}>
                  Welcome to Ognomy Sleep
                </Typography>
                <Typography variant="h6" pb={1}>
                  Your Account Has Been Created and Confirmed
                </Typography>
                <Typography variant="h6">
                  Before you schedule an appointment, we'll need some additional
                  information. You can complete your profile now or we'll remind
                  you via email.
                </Typography>
              </CardContent>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                  p: 1,
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={() =>
                    history.replace("/complete-profile?step=insurance")
                  }
                >
                  Complete My Profile
                </Button>
                <Button
                  variant="text"
                  onClick={() => history.replace("/patientDashboardPage")}
                >
                  I'll Finish Later
                </Button>
              </Box>
            </Card>
          </Grid>
        )}
      </Grid>

      {step !== totalSteps && (
        <Stack spacing={1} direction="row-reverse" sx={actionBtnStyles}>
          <LoadingButton
            type="submit"
            disabled={step === 1 || totalSteps === step || submitting}
            variant="contained"
            loading={submitting}
            onClick={() => {
              if (step === 2) {
                handleDynamicFormSubmission(EMAIL_VERIF_FORM_ID);
              } else if (step === 3) {
                handleDynamicFormSubmission(CODE_VERIFICATION_FORM_ID);
              } else if (step === 4) {
                handleDynamicFormSubmission(PATIENT_DETAILS_FORM_ID);
              } else setStep(step + 1);
            }}
          >
            Continue
          </LoadingButton>
          {step > 1 && (
            <LoadingButton
              type="submit"
              variant="contained"
              disabled={confirmedAccount}
              onClick={() => {
                updateStep(false);
              }}
            >
              Back
            </LoadingButton>
          )}
        </Stack>
      )}
    </Box>
  );
};

const actionBtnStyles = {
  padding: 1,
  paddingBottom: 1.5,
  bottom: 0,
  left: 0,
  position: "fixed",
  width: "100%",
  zIndex: 100,
  backgroundColor: "background.default",
  justifyContent: "space-between",
};

export default GenericRegistration;
